import {useMemo} from 'react';
import {matchPath, useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';
import authenticatedRoutes from '../routes/authenticated.routes';
import unauthenticatedRoutes from '../routes/unauthenticated.routes';
import publicRoutes from '../routes/public.routes';

/**
 * The manifest entry for the current URL, its params, and the full route list
 * it was matched against.
 *
 * The list is built for the widest access (`canAccessSelfCheck: true`) so
 * lookups like title and breadcrumbs see every page the app has, regardless
 * of who is looking. Access is enforced where routes are mounted, not here.
 * Matching runs in manifest order so the entries that share a path keep
 * first-match-wins.
 *
 * @returns {{route: object|undefined, routes: object[], params: object}}
 */
function useCurrentRoute() {
  const {pathname} = useLocation();
  const auth = useSelector(state => state.auth);

  const routes = useMemo(
    () => [
      ...authenticatedRoutes({...auth, canAccessSelfCheck: true}),
      ...unauthenticatedRoutes(auth),
      ...publicRoutes(),
    ],
    [auth]
  );

  return useMemo(() => {
    const route = routes.find(({path}) =>
      matchPath({path, end: true}, pathname)
    );
    const params = route
      ? matchPath({path: route.path, end: true}, pathname).params
      : {};
    return {route, routes, params};
  }, [routes, pathname]);
}

export default useCurrentRoute;
